/**
 * @file SectionBlockEditor.tsx
 * @description Редактор секции v2 в панели конструктора (phase19.16 Р2;
 * uiux-design-r2 §2, §4, §6, §9, §10): действия с блоком, вкладки
 * [Содержимое][Оформление], панель выбранного элемента (выбор общий с
 * канвой), подвал «Сохранить блок · Отменить правки» одной строкой.
 * На телефоне вкладки и «⋯» — одна строка 48 (касание), чтобы под настройки
 * оставалось больше места (uiux §2). Форма — `useSectionDraft`; пока в
 * форме есть правки, текст с канвы идёт в неё, а клик по элементу на канве
 * ищет элемент в ней же (`formSink`).
 * Р2а: контекст секции (шаблон товара — элементы данных товара, «уже
 * есть в блоке»), «Перенести в другой блок» (`useSectionExtras`).
 * SB122 (ревью партии): «Сохранить блок» не прошло из-за замечаний — видимая
 * панель раскрывает первую группу с ошибкой (`useSectionReveal`).
 * SB120 этап 1 (uiux-sb120-stage1 §4, §6; §7.19 фазы): живой предпросмотр
 * ползунков на канве (`useCanvasPreview`, плашка «Канва не отвечает…»),
 * предупреждения оформления (`useSectionLookWarnings`) и список замечаний
 * с переходом к полю (`SectionIssueList` → элемент → `jump`).
 * SB120 этап 3а: форма с предупреждениями элементов — `useSectionFormValue`; они же в списке замечаний.
 */

import { useCallback, useEffect, useState, type MutableRefObject } from "react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Tabs } from "../../ui/Tabs";
import { useNotify } from "../../ui/NotifyProvider";
import { useSiteView } from "../../site-view/site-view-context";
import { blockTitle } from "../../site-pages/block-meta";
import { useBuilder } from "../builder-context";
import { ElementPanel } from "./ElementPanel";
import { MoveToBlockMenu } from "./MoveToBlockMenu";
import { SectionLookTab } from "./SectionLookTab";
import { SectionActions } from "./SectionActions";
import { SectionContentTab } from "./SectionContentTab";
import { SectionFormContext } from "./section-form-context";
import { SectionIssueList } from "./SectionIssueList";
import { CANVAS_PREVIEW_STALLED_TEXT, useCanvasPreview } from "../../../hooks/site-builder/useCanvasPreview";
import { useSectionFormValue } from "../../../hooks/site-builder/useSectionFormValue";
import { sectionIssueItems } from "../../../lib/site-builder/look-warnings";
import { useSectionDraft } from "../../../hooks/site-builder/useSectionDraft";
import { useSectionExtras } from "../../../hooks/site-builder/useSectionExtras";
import { useSectionFormSink } from "../../../hooks/site-builder/useSectionFormSink";
import { useSectionReveal, type SectionTab } from "../../../hooks/site-builder/useSectionReveal";
import { sectionContextOf } from "../../../lib/site-builder/template-rules";
import type { SitePageBlock } from "../../../lib/api-site-pages";
import { otherH1Block } from "../../../lib/site-builder/page-h1";
import { elementIdAtPath } from "../../../lib/site-builder/section-edit";
import { SECTION_FOOT_BUTTON_CLASS } from "../../../lib/site-builder/section-meta";
import { spaceHighlightMessage, elementHighlightMessage, postToCanvas, type SpaceHighlightPart } from "../../../lib/site-view/canvas-messages";

const TABS = [
  { value: "content" as const, label: "Содержимое" },
  { value: "look" as const, label: "Оформление" },
];

interface Props {
  block: SitePageBlock;
  onDirtyChange: (dirty: boolean) => void;
  controller: MutableRefObject<{ save: () => Promise<boolean> } | null>;
  /** Сохранено: перечитать страницу. */
  onSaved: () => void;
  /** 409 `block_changed`: свежая страница и окно-пояснение (правки остаются). */
  onConflict: () => void;
}

export function SectionBlockEditor({ block, onDirtyChange, controller, onSaved, onConflict }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const [tab, setTab] = useState<SectionTab>("content");
  const [fresh, setFresh] = useState(false);
  // Группа «Оформления», которую раскрыть при переходе («изменить ›» → «Колонки», SB122).
  const [lookGroup, setLookGroup] = useState<string | null>(null);
  const { pages, formSink, setElement, iframeRef } = b;
  const context = sectionContextOf(pages.page?.kind);
  const draft = useSectionDraft({ block, iframeRef, notify, save: b.blocks.saveBlock, onSaved, onConflict, context });
  controller.current = { save: draft.save };
  useEffect(() => onDirtyChange(draft.dirty), [draft.dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // Текст с канвы при несохранённой форме — в форму, а не на сайт.
  const { payload, setPayload, dirty } = draft;
  useSectionFormSink({ formSink, blockId: block.id, dirty, payload, setPayload });

  const elementId = b.element?.blockId === block.id ? b.element.elementId : null;
  const selectElement = useCallback(
    (id: string | null, isFresh = false) => {
      setFresh(isFresh);
      setElement(id ? { blockId: block.id, elementId: id } : null);
    },
    [block.id, setElement],
  );
  useEffect(() => {
    postToCanvas(iframeRef.current, elementHighlightMessage(block.id, elementId));
  }, [block.id, elementId, iframeRef]);

  const highlight = useCallback((part: SpaceHighlightPart | null) => postToCanvas(iframeRef.current, spaceHighlightMessage(block.id, part)), [block.id, iframeRef]);
  const theme = view.parts?.theme.theme ?? null;
  const title = blockTitle({ type: block.type, payload });
  const extras = useSectionExtras({ block, payload, title, dirty, save: draft.save, selectElement });
  const { formExtra } = extras;
  const { reveal, revealDone, revealPath, jump } = useSectionReveal({ failedSaves: draft.failedSaves, issues: draft.issues, tab, setTab, inElement: elementId !== null });
  const { preview, stalled } = useCanvasPreview(block.id, iframeRef);
  const jumpExtra = { reveal, revealDone, revealPath, preview };
  const { form, look, elementWarnings } = useSectionFormValue({ blockId: block.id, payload, theme, page: pages.page, issues: draft.issues, warnings: draft.warnings, highlight, formExtra, reveal: jumpExtra });
  // Переход из списка замечаний: путь элемента — его панель, иначе панель секции.
  const onJump = (path: string) => {
    const id = elementIdAtPath(payload, path);
    selectElement(id);
    jump(path, id !== null);
  };
  const pageBlocks = pages.page?.blocks ?? [];
  const current = { id: block.id, payload, title };

  const askPhoto = b.bgPhotoRequest === block.id;
  useEffect(() => {
    if (askPhoto) setTab("look");
  }, [askPhoto]);
  const clearLookGroup = useCallback(() => setLookGroup(null), []);
  const editColumns = () => {
    setLookGroup("columns");
    setTab("look");
  };

  return (
    <SectionFormContext.Provider value={form}>
      <div className="flex min-h-full min-w-0 flex-col gap-3">
        {elementId ? (
          <ElementPanel
            payload={payload}
            elementId={elementId}
            sectionTitle={title}
            onChange={setPayload}
            onBack={() => selectElement(null)}
            onSelect={(id) => selectElement(id)}
            fresh={fresh}
            onFreshDone={() => setFresh(false)}
            otherH1={otherH1Block(pageBlocks, current, { elementId })}
            dataCounts={pageBlocks.find((x) => x.id === block.id)?.dataCounts ?? block.dataCounts}
            countsStale={dirty}
          />
        ) : (
          <>
            <div className="max-sm:hidden">
              <SectionActions block={block} />
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Tabs aria-label="Настройки секции" items={TABS} value={tab} onChange={setTab} className="min-w-0 flex-1" />
              <div className="sm:hidden">
                <SectionActions block={block} compact />
              </div>
            </div>
            {tab === "content" ? (
              <SectionContentTab
                payload={payload}
                onChange={setPayload}
                selectedId={null}
                onSelect={selectElement}
                otherH1ForHeader={otherH1Block(pageBlocks, current, { header: true })}
                onEditColumns={editColumns}
              />
            ) : (
              <SectionLookTab
                payload={payload}
                onChange={setPayload}
                appearance={draft.appearance}
                onAppearance={draft.setAppearance}
                confirm={b.confirm}
                askPhoto={askPhoto}
                onAsked={() => b.setBgPhotoRequest(null)}
                onPageBackground={() => void b.select({ sel: { kind: "page" } })}
                focusGroup={lookGroup}
                onFocused={clearLookGroup}
              />
            )}
          </>
        )}
        {stalled ? <NoticeStrip tone="warning">{CANVAS_PREVIEW_STALLED_TEXT}</NoticeStrip> : null}
        <SectionIssueList items={sectionIssueItems(draft.issues, look.warnings, elementWarnings)} onJump={onJump} />
        {/* min-w-0 у подвала и кнопок: на 360 и в панели 296 на 768 подвал не шире панели (QA D6, QA 3б A3). */}
        <div className="sticky bottom-0 mt-auto flex min-w-0 gap-2 bg-surface-lowest pt-2 [box-shadow:0_-1px_0_var(--outline-variant)]">
          <Button className={SECTION_FOOT_BUTTON_CLASS} disabled={!dirty} loading={draft.saving} onClick={() => void draft.save()}>
            Сохранить блок
          </Button>
          {dirty ? (
            <Button variant="ghost" className={SECTION_FOOT_BUTTON_CLASS} onClick={draft.reset}>
              <span className="truncate">Отменить правки</span>
            </Button>
          ) : null}
        </div>
      </div>
      <MoveToBlockMenu {...extras.dialog} />
    </SectionFormContext.Provider>
  );
}
